import { useState, useRef, useEffect } from 'react'
import { Upload, X, Loader2, Euro, Ruler, Check, AlertTriangle } from 'lucide-react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { analyzeFlashCrop, generatePaddingOverlay, cropImageToDataUrl, type FlashCropAnalysis } from '@/lib/flashCropValidation'
import { uploadFlashFromDataUrl } from '@/lib/portfolioUpload'

interface UploadSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  type: 'flash' | 'tattoo' | 'healed'
  onUpload?: (files: File[], metadata?: Array<{ minPrice: string; minSize: string }>) => Promise<void>
  onTypeChange?: (type: 'flash' | 'tattoo' | 'healed') => void
  artistId: string
  isAdmin?: boolean
}

interface FlashMetadata {
  minPrice: string
  minSize: string
}

interface CropValidation {
  isValid: boolean
  isAnalyzing: boolean
  analysis?: FlashCropAnalysis
  error?: string
}

export function UploadSheet({ isOpen, onOpenChange, type: initialType, onUpload, onTypeChange, artistId, isAdmin }: UploadSheetProps) {
  const [uploadType, setUploadType] = useState<'flash' | 'tattoo' | 'healed'>(initialType)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<string[]>([])
  const [croppedDataUrls, setCroppedDataUrls] = useState<Map<number, string>>(new Map())
  const [isUploading, setIsUploading] = useState(false)
  const [flashMetadata, setFlashMetadata] = useState<Map<number, FlashMetadata>>(new Map())
  const [cropValidations, setCropValidations] = useState<Map<number, CropValidation>>(new Map())
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [cropOverrides, setCropOverrides] = useState<Set<number>>(new Set())
  const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map())
  const imageRefs = useRef<Map<number, HTMLImageElement>>(new Map())

  // Update uploadType when initialType changes
  useEffect(() => {
    setUploadType(initialType)
  }, [initialType])

  const processFiles = (files: File[]) => {
    const startIndex = previews.length
    
    // Create previews
    files.forEach((file, i) => {
      const reader = new FileReader()
      reader.onload = async (e) => {
        const result = e.target?.result as string
        setPreviews(prev => [...prev, result])
        
        // Auto-analyze crop for flash images
        if (uploadType === 'flash') {
          const imageIndex = startIndex + i
          await analyzeCrop(imageIndex, result)
        }
      }
      reader.readAsDataURL(file)
    })
    
    setSelectedFiles(prev => [...prev, ...files])
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    processFiles(files)
  }

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
    setPreviews(prev => prev.filter((_, i) => i !== index))
    
    // Remove metadata for this image
    setFlashMetadata(prev => {
      const next = new Map(prev)
      next.delete(index)
      // Reindex remaining items
      const reindexed = new Map<number, FlashMetadata>()
      Array.from(next.entries()).forEach(([key, value]) => {
        if (key > index) {
          reindexed.set(key - 1, value)
        } else {
          reindexed.set(key, value)
        }
      })
      return reindexed
    })
    
    // Remove crop validation for this image
    setCropValidations(prev => {
      const next = new Map(prev)
      next.delete(index)
      // Reindex remaining items
      const reindexed = new Map<number, CropValidation>()
      Array.from(next.entries()).forEach(([key, value]) => {
        if (key > index) {
          reindexed.set(key - 1, value)
        } else {
          reindexed.set(key, value)
        }
      })
      return reindexed
    })
    
    // Remove crop override for this image
    setCropOverrides(prev => {
      const next = new Set(prev)
      next.delete(index)
      // Reindex remaining items
      const reindexed = new Set<number>()
      Array.from(next).forEach(key => {
        if (key > index) {
          reindexed.add(key - 1)
        } else {
          reindexed.add(key)
        }
      })
      return reindexed
    })
    
    // Clean up refs
    canvasRefs.current.delete(index)
    imageRefs.current.delete(index)
  }

  const handleUpload = async () => {
    console.log('[UploadSheet] handleUpload called')
    console.log('[UploadSheet] Selected files:', selectedFiles.length)
    console.log('[UploadSheet] Artist ID:', artistId)
    console.log('[UploadSheet] Upload type:', uploadType)
    
    if (selectedFiles.length === 0 || !onUpload) {
      console.log('[UploadSheet] Aborting: No files or no onUpload handler')
      return
    }
    
    // Clear any previous errors
    setUploadError(null)
    
    // Validate flash pricing and crops if uploading flashes
    if (uploadType === 'flash') {
      // Check if all images have price and size
      const allHaveMetadata = selectedFiles.every((_, index) => {
        const metadata = flashMetadata.get(index)
        return metadata && metadata.minPrice && metadata.minSize
      })
      
      if (!allHaveMetadata) {
        setUploadError('Please set price and size for all flashes')
        return
      }
      
      // Check if all crops are valid (unless overridden by admin)
      const allCropsValid = selectedFiles.every((_, index) => {
        const hasOverride = cropOverrides.has(index)
        if (hasOverride) return true // Skip validation if admin has overridden
        
        const validation = cropValidations.get(index)
        return validation && validation.isValid
      })
      
      if (!allCropsValid) {
        setUploadError('All flashes must have valid crop padding. Please remove flashes with invalid crops (red overlay) or override the crop as an admin.')
        return
      }
    }
    
    // Validate healed items - no special validation needed
    if (uploadType === 'healed') {
      // Healed items just need files selected, no additional validation
    }
    
    setIsUploading(true)
    console.log('[UploadSheet] Starting upload process...')
    
    try {
      // For flash uploads with cropped images, upload to Firebase first
      if (uploadType === 'flash') {
        console.log('[UploadSheet] Processing flash upload')
        console.log('[UploadSheet] Flash metadata:', Object.fromEntries(flashMetadata))
        console.log('[UploadSheet] Cropped data URLs count:', croppedDataUrls.size)
        
        const metadataArray = selectedFiles.map((_, index) => {
          const metadata = flashMetadata.get(index)
          return {
            minPrice: metadata?.minPrice || '',
            minSize: metadata?.minSize || ''
          }
        })
        console.log('[UploadSheet] Metadata array:', metadataArray)
        
        // Upload each flash individually with its cropped version
        for (let index = 0; index < selectedFiles.length; index++) {
          const croppedDataUrl = croppedDataUrls.get(index)
          const metadata = metadataArray[index]
          
          console.log(`[UploadSheet] Processing flash ${index + 1}/${selectedFiles.length}`)
          console.log(`[UploadSheet] Has cropped URL: ${!!croppedDataUrl}`)
          console.log(`[UploadSheet] Metadata:`, metadata)
          
          if (croppedDataUrl) {
            console.log(`[UploadSheet] Uploading cropped flash ${index + 1}...`)
            console.log(`[UploadSheet] Cropped data URL length: ${croppedDataUrl.length}`)
            
            // Upload the cropped image directly to flashes collection
            const docId = await uploadFlashFromDataUrl(
              croppedDataUrl,
              artistId,
              metadata.minPrice,
              metadata.minSize
            )
            
            console.log(`[UploadSheet] ✅ Uploaded cropped flash ${index + 1}/${selectedFiles.length} with ID:`, docId)
          } else {
            console.log(`[UploadSheet] No cropped URL, uploading original file ${index + 1}...`)
            // Upload the original file if no cropping was done
            await onUpload([selectedFiles[index]], [metadata])
            console.log(`[UploadSheet] ✅ Uploaded original file ${index + 1}/${selectedFiles.length}`)
          }
        }
        console.log('[UploadSheet] ✅ All flash uploads completed successfully')
      } else if (uploadType === 'healed') {
        console.log('[UploadSheet] Processing healed portfolio upload')
        // Healed items are handled through onUpload callback
        const metadataArray = selectedFiles.map(() => ({
          minPrice: '',
          minSize: ''
        }))
        
        console.log('[UploadSheet] Uploading healed portfolio files...')
        await onUpload(selectedFiles, metadataArray)
        console.log('[UploadSheet] ✅ All healed portfolio uploads completed successfully')
      } else {
        console.log('[UploadSheet] Processing tattoo upload')
        // For tattoos, just upload the original files
        const metadataArray = selectedFiles.map((_, index) => {
          const metadata = flashMetadata.get(index)
          return {
            minPrice: metadata?.minPrice || '',
            minSize: metadata?.minSize || ''
          }
        })
        
        console.log('[UploadSheet] Uploading tattoo files...')
        await onUpload(selectedFiles, metadataArray)
        console.log('[UploadSheet] ✅ All tattoo uploads completed successfully')
      }
      
      console.log('[UploadSheet] ✅ Upload process completed, clearing state...')
      // Clear on success
      setSelectedFiles([])
      setPreviews([])
      setCroppedDataUrls(new Map())
      setFlashMetadata(new Map())
      setCropValidations(new Map())
      setCropOverrides(new Set())
      setUploadError(null)
      canvasRefs.current.clear()
      imageRefs.current.clear()
      onOpenChange(false)
    } catch (error) {
      console.error('[UploadSheet] ❌ Upload FAILED:', error)
      if (error instanceof Error) {
        console.error('[UploadSheet] Error message:', error.message)
        console.error('[UploadSheet] Error stack:', error.stack)
      }
      const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.'
      setUploadError(errorMessage)
    } finally {
      setIsUploading(false)
    }
  }

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFiles([])
      setPreviews([])
      setCroppedDataUrls(new Map())
      setFlashMetadata(new Map())
      setCropValidations(new Map())
      setCropOverrides(new Set())
      setUploadError(null)
      canvasRefs.current.clear()
      imageRefs.current.clear()
      onOpenChange(false)
    }
  }

  const analyzeCrop = async (index: number, imageUrl: string) => {
    console.log(`[UploadSheet] Starting crop analysis for image ${index}`)
    console.log(`[UploadSheet] Image URL (first 100 chars):`, imageUrl.substring(0, 100))
    
    // Set analyzing state
    setCropValidations(prev => {
      const next = new Map(prev)
      next.set(index, { isValid: false, isAnalyzing: true })
      return next
    })

    try {
      console.log(`[UploadSheet] Calling analyzeFlashCrop for image ${index}...`)
      const result = await analyzeFlashCrop(imageUrl)
      console.log(`[UploadSheet] ✅ Crop analysis result for image ${index}:`, result)
      
      let finalAnalysis = result
      
      // If valid and needs cropping, crop and reupload
      if (result.isValid && result.optimalCrop) {
        const { x, y, width, height, appliedPadding } = result.optimalCrop
        
        // Check if cropping is actually needed (dimensions different from original)
        const needsCropping = x > 0 || y > 0 || 
                             width < result.imageDimensions.width || 
                             height < result.imageDimensions.height
        
        if (needsCropping) {
          // Crop the image
          const croppedDataUrl = await cropImageToDataUrl(
            imageUrl,
            x,
            y,
            width,
            height
          )
          
          // Update the preview URL to show the cropped version
          setPreviews(prev => {
            const next = [...prev]
            next[index] = croppedDataUrl
            return next
          })
          
          // Store the cropped data URL in the map
          setCroppedDataUrls(prev => {
            const next = new Map(prev)
            next.set(index, croppedDataUrl)
            return next
          })
          
          // Recalculate analysis for the cropped image
          // The bounding box in the cropped image starts at the applied padding
          const croppedBoundingBox = {
            x: appliedPadding.left,
            y: appliedPadding.top,
            width: result.boundingBox.width,
            height: result.boundingBox.height
          }
          
          // Calculate new padding values for the cropped image
          const croppedPadding = {
            top: {
              value: appliedPadding.top,
              percentage: (appliedPadding.top / height) * 100,
              isValid: true,
              needsMore: false
            },
            right: {
              value: appliedPadding.right,
              percentage: (appliedPadding.right / width) * 100,
              isValid: true,
              needsMore: false
            },
            bottom: {
              value: appliedPadding.bottom,
              percentage: (appliedPadding.bottom / height) * 100,
              isValid: true,
              needsMore: false
            },
            left: {
              value: appliedPadding.left,
              percentage: (appliedPadding.left / width) * 100,
              isValid: true,
              needsMore: false
            }
          }
          
          // Update analysis with cropped image dimensions and adjusted bounding box
          finalAnalysis = {
            ...result,
            boundingBox: croppedBoundingBox,
            imageDimensions: { width, height },
            padding: croppedPadding,
            optimalCrop: undefined // No more cropping needed
          }
        }
      }
      
      setCropValidations(prev => {
        const next = new Map(prev)
        next.set(index, { 
          isValid: finalAnalysis.isValid, 
          isAnalyzing: false,
          analysis: finalAnalysis 
        })
        return next
      })
      
      // Draw overlay after analysis
      setTimeout(() => drawPaddingOverlay(index, finalAnalysis), 100)
    } catch (err) {
      console.error(`[UploadSheet] ❌ Crop analysis FAILED for image ${index}:`, err)
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze'
      console.error(`[UploadSheet] Error message for image ${index}:`, errorMessage)
      if (err instanceof Error && err.stack) {
        console.error(`[UploadSheet] Error stack:`, err.stack)
      }
      setCropValidations(prev => {
        const next = new Map(prev)
        next.set(index, { 
          isValid: false, 
          isAnalyzing: false,
          error: errorMessage
        })
        return next
      })
    }
  }

  const drawPaddingOverlay = (index: number, analysisData: FlashCropAnalysis) => {
    const canvas = canvasRefs.current.get(index)
    const image = imageRefs.current.get(index)
    if (!canvas || !image || !analysisData) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size to match image display size
    const rect = image.getBoundingClientRect()
    canvas.width = rect.width
    canvas.height = rect.height

    // Calculate scale factors
    const scaleX = rect.width / analysisData.imageDimensions.width
    const scaleY = rect.height / analysisData.imageDimensions.height

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    // Generate padding overlay trapezoids
    const overlays = generatePaddingOverlay(analysisData)

    // Draw each padding area as trapezoids
    Object.entries(overlays).forEach(([, overlay]) => {
      // Set color based on validation
      ctx.fillStyle = overlay.isValid ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.5)'
      ctx.strokeStyle = overlay.isValid ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)'
      ctx.lineWidth = 2

      // Draw trapezoid path
      ctx.beginPath()
      
      // Scale and move to first point
      const firstPoint = overlay.points[0]
      ctx.moveTo(firstPoint.x * scaleX, firstPoint.y * scaleY)
      
      // Draw lines to all other points
      for (let i = 1; i < overlay.points.length; i++) {
        const point = overlay.points[i]
        ctx.lineTo(point.x * scaleX, point.y * scaleY)
      }
      
      // Close the path
      ctx.closePath()
      
      // Fill and stroke the trapezoid
      ctx.fill()
      ctx.stroke()
    })
  }

  // Redraw overlays when window resizes or cropValidations change
  useEffect(() => {
    const handleResize = () => {
      cropValidations.forEach((validation, index) => {
        if (validation.analysis) {
          drawPaddingOverlay(index, validation.analysis)
        }
      })
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [cropValidations])

  const toggleCropOverride = (index: number) => {
    setCropOverrides(prev => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  // Check if all flashes are ready to upload (valid crop + metadata)
  const allFlashesReady = uploadType === 'flash' ? selectedFiles.every((_, index) => {
    const metadata = flashMetadata.get(index)
    const hasMetadata = metadata && metadata.minPrice && metadata.minSize
    
    // If admin has overridden crop validation, only need metadata
    if (cropOverrides.has(index)) {
      return hasMetadata
    }
    
    // Otherwise need valid crop + metadata
    const validation = cropValidations.get(index)
    const hasValidCrop = validation && validation.isValid && !validation.isAnalyzing
    return hasValidCrop && hasMetadata
  }) : true

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent 
        side="bottom" 
        className="h-[90vh] flex flex-col p-6 max-w-[calc(700px+1.5rem)] mx-auto left-0 right-0" 
        hideCloseButton
      >
        <SheetHeader className="flex flex-col space-y-4 pb-4">
          <SheetTitle className="text-xl font-semibold">
            Upload Designs
          </SheetTitle>
          
          {/* Type Selection Tabs - Centered */}
          <div className="flex gap-4 border-b border-border justify-center">
            <button
              onClick={() => {
                setUploadType('flash')
                onTypeChange?.('flash')
              }}
              className={`pb-2 font-medium transition-colors ${
                uploadType === 'flash'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Flashes
            </button>
            <button
              onClick={() => {
                setUploadType('tattoo')
                onTypeChange?.('tattoo')
              }}
              className={`pb-2 font-medium transition-colors ${
                uploadType === 'tattoo'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Tattoos
            </button>
            <button
              onClick={() => {
                setUploadType('healed')
                onTypeChange?.('healed')
              }}
              className={`pb-2 font-medium transition-colors ${
                uploadType === 'healed'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Healed
            </button>
          </div>
        </SheetHeader>
        
        <div className="flex-1 overflow-hidden flex flex-col gap-4">
          {/* Error Message */}
          {uploadError && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
              <p className="font-medium">Upload Error</p>
              <p>{uploadError}</p>
            </div>
          )}
          {/* Upload Button */}
          <div>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif,image/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
              id="upload-images"
              disabled={isUploading}
            />
            <label htmlFor="upload-images" className="block">
              <Button
                className="w-full h-12 text-base"
                disabled={isUploading}
                asChild
              >
                <span className="cursor-pointer flex items-center justify-center gap-2">
                  <Upload className="w-5 h-5" />
                  Select
                </span>
              </Button>
            </label>
          </div>

          {/* Preview Grid */}
          {previews.length > 0 && (
            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                {previews.map((preview, index) => {
                  const metadata = flashMetadata.get(index) || { minPrice: '', minSize: '' }
                  const validation = cropValidations.get(index)
                  const file = selectedFiles[index]
                  
                  return (
                    <div key={index} className="flex flex-col space-y-1">
                      {/* Image Container */}
                      <div className="relative aspect-square rounded-lg overflow-hidden border-2 border-border">
                        <img
                          ref={(el) => {
                            if (el) imageRefs.current.set(index, el)
                          }}
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-contain"
                          onLoad={() => {
                            // Redraw overlay when image loads
                            if (validation?.analysis) {
                              drawPaddingOverlay(index, validation.analysis)
                            }
                          }}
                        />
                        
                        {/* Canvas overlay for padding visualization */}
                        {uploadType === 'flash' && (
                          <canvas
                            ref={(el) => {
                              if (el) canvasRefs.current.set(index, el)
                            }}
                            className="absolute top-0 left-0 pointer-events-none rounded-lg"
                            style={{ width: '100%', height: '100%' }}
                          />
                        )}
                        
                        {/* Crop validation badge - Top Left */}
                        {uploadType === 'flash' && validation && (
                          <div className="absolute top-2 left-2 z-10">
                            {validation.isAnalyzing ? (
                              <Badge variant="secondary" className="bg-blue-500 text-white text-xs">
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Checking
                              </Badge>
                            ) : validation.error ? (
                              <Badge variant="destructive" className="text-xs max-w-[calc(100%-3rem)]" title={validation.error}>
                                <AlertTriangle className="w-3 h-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{validation.error.length > 30 ? validation.error.substring(0, 30) + '...' : validation.error}</span>
                              </Badge>
                            ) : validation.isValid ? (
                              <Badge variant="secondary" className="bg-green-500 text-white text-xs">
                                <Check className="w-3 h-3 mr-1" />
                                Valid
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-red-500 text-white text-xs">
                                <X className="w-3 h-3 mr-1" />
                                Bad Crop
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        {/* Show full error message below image if there's an error */}
                        {uploadType === 'flash' && validation?.error && (
                          <div className="absolute bottom-0 left-0 right-0 bg-red-500/90 text-white text-xs p-2 z-10">
                            {validation.error}
                          </div>
                        )}
                        
                        {/* Delete button - Top Right */}
                        {!isUploading && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              removeFile(index)
                            }}
                            className="absolute top-2 right-2 bg-background text-foreground w-7 h-7 rounded-full flex items-center justify-center shadow-lg hover:bg-background/80 transition-colors z-10"
                          >
                            <X className="w-4 h-4" strokeWidth={2} />
                          </button>
                        )}
                      </div>
                      
                      {/* Filename */}
                      {file && (
                        <p className="text-[10px] text-muted-foreground truncate" title={file.name}>
                          {file.name}
                        </p>
                      )}
                      
                      {/* Flash Pricing Form - Under each image */}
                      {uploadType === 'flash' && (
                        <div className="grid grid-cols-2 gap-1">
                          <div className="space-y-0.5">
                            <Label className="text-[11px] flex items-center gap-0.5 leading-tight">
                              <Ruler className="w-2.5 h-2.5" />
                              Min Size
                            </Label>
                            <Input
                              value={metadata.minSize}
                              onChange={(e) => {
                                const numbersOnly = e.target.value.replace(/\D/g, '')
                                const limited = numbersOnly.slice(0, 3)
                                setFlashMetadata(prev => {
                                  const next = new Map(prev)
                                  const current = next.get(index) || { minPrice: '', minSize: '' }
                                  next.set(index, { ...current, minSize: limited })
                                  return next
                                })
                              }}
                              placeholder="5"
                              disabled={isUploading}
                              inputMode="numeric"
                              maxLength={3}
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-0.5">
                            <Label className="text-[11px] flex items-center gap-0.5 leading-tight">
                              <Euro className="w-2.5 h-2.5" />
                              Min Price
                            </Label>
                            <Input
                              value={metadata.minPrice}
                              onChange={(e) => {
                                const numbersOnly = e.target.value.replace(/\D/g, '')
                                const limited = numbersOnly.slice(0, 4)
                                setFlashMetadata(prev => {
                                  const next = new Map(prev)
                                  const current = next.get(index) || { minPrice: '', minSize: '' }
                                  next.set(index, { ...current, minPrice: limited })
                                  return next
                                })
                              }}
                              placeholder="150"
                              disabled={isUploading}
                              inputMode="numeric"
                              maxLength={4}
                              className="text-sm"
                            />
                          </div>
                        </div>
                      )}

                      {/* Admin Crop Override Button */}
                      {uploadType === 'flash' && isAdmin && (
                        <Button
                          variant={cropOverrides.has(index) ? "default" : "outline"}
                          size="sm"
                          onClick={() => toggleCropOverride(index)}
                          className="w-full text-xs"
                          disabled={isUploading}
                        >
                          {cropOverrides.has(index) ? '✓ Override Enabled' : 'Override Crop'}
                        </Button>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            variant="outline"
            className="flex-1 h-14 text-base"
            onClick={handleClose}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            className="flex-1 h-14 text-base"
            onClick={handleUpload}
            disabled={selectedFiles.length === 0 || isUploading || !allFlashesReady}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              `Upload (${selectedFiles.length})`
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

